'use client';

import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box } from '@mui/material';
import { LoginOutlined } from '@mui/icons-material';
import { useConfigStore } from '@/stores/configStore';
import { useAuthStore } from '@/stores/authStore';
import type { SessionLostReason } from '@/lib/auth/sessionAuth';
import { navigateToShibbolethSignIn } from '@/lib/auth/signInFlow';
import { useSystemConfig } from '@/hooks/useSystemConfig';

function getMessage(reason: SessionLostReason, timeoutHours: number): string {
  switch (reason) {
    case 'inactivity':
      return `You were signed out after ${timeoutHours} hour${timeoutHours === 1 ? '' : 's'} of inactivity. Sign in again to see your data.`;
    case 'degraded':
      return 'Your session could not be renewed without signing in again. You may be viewing public data only until you sign in.';
    default:
      return 'Your session has ended. Sign in again to see your data.';
  }
}

/**
 * Blocking dialog when a signed-in user's session is lost or degrades to anonymous.
 * Not shown for users who were already anonymous.
 */
export default function SessionLostDialog() {
  const { systemConfig } = useConfigStore();
  const systemConfigHook = useSystemConfig();
  const sessionLostReason = useAuthStore((s) => s.sessionLostReason);
  const sessionLostDialogOpen = useAuthStore((s) => s.sessionLostDialogOpen);
  const login = useAuthStore((s) => s.login);
  const isInProgress = useAuthStore((s) => s.isInProgress);
  const acceptPublicSession = useAuthStore((s) => s.acceptPublicSession);
  const clearSessionLost = useAuthStore((s) => s.clearSessionLost);

  const allowAnonymous = systemConfig?.allowAnonymous ?? false;
  const enableShibboleth = systemConfig?.enableShibboleth ?? false;
  const timeoutHours = systemConfigHook?.sessionTimeoutInHours ?? 8;

  const open = sessionLostDialogOpen && sessionLostReason != null;

  const handleSignIn = async () => {
    try {
      await login();
      clearSessionLost();
      if (window.opener) {
        window.close();
      }
    } catch {
      /* login sets error state */
    }
  };

  const handleShibbolethSignin = () => {
    navigateToShibbolethSignIn(systemConfig?.apiEndpoint, window.location.pathname + window.location.search);
  };

  const handleContinuePublic = async () => {
    await acceptPublicSession();
  };

  return (
    <Dialog open={open} disableEscapeKeyDown aria-labelledby="session-lost-dialog-title" maxWidth="sm" fullWidth>
      <DialogTitle id="session-lost-dialog-title">You&apos;ve been signed out</DialogTitle>
      <DialogContent>
        <Typography variant="body1" color="text.secondary">
          {getMessage(sessionLostReason, timeoutHours)}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', px: 3, pb: 2, gap: 1 }}>
        <Button
          variant="contained"
          size="large"
          onClick={handleSignIn}
          disabled={isInProgress}
          startIcon={<LoginOutlined />}
        >
          {isInProgress ? 'Signing in...' : 'Sign in with Microsoft'}
        </Button>
        {enableShibboleth ? (
          <Button
            variant="contained"
            size="large"
            onClick={handleShibbolethSignin}
            disabled={isInProgress}
            startIcon={<LoginOutlined />}
          >
            Sign in with InCommon
          </Button>
        ) : null}
        {allowAnonymous ? (
          <Box sx={{ pt: 1 }}>
            <Button variant="outlined" fullWidth onClick={handleContinuePublic} disabled={isInProgress}>
              Continue with public data
            </Button>
          </Box>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}
